// Executive Challenge backend — Phase 1 LOCAL STUB.
//
// The real service (Phase 2) is Supabase: phone-verified identities, a
// `challenges` row per hosted contest, `challenge_entries` for results, a
// per-challenge leaderboard, and 24h share links. That implementation lives in
// ./supabase.ts behind the same interface; index.ts picks whichever config
// enables, so the swap is configuration, not a rework of the callers.
//
// Stub mechanics: there is no server, so the join code itself carries the one
// fact a joiner needs that the seed can't derive — the host's team. A code is a
// random 6-digit number congruent to the tid mod 30 (codes still look random;
// tid = code % 30). Everything else a joiner needs (the seed) is derived from
// the code by seedForCode. This makes host → join work fully offline, across
// worker restarts, with no persistence.

import {
	CHALLENGE_CODE_LENGTH,
	isValidChallengeCode,
	seedForCode,
} from "../../../common/executiveChallenge.ts";
import { ChallengeCodeError } from "./types.ts";
import type { ChallengeEntry, ChallengeRow } from "./types.ts";

// The real league always has 30 teams (tid 0-29); the encoding depends on it.
const NUM_TEAMS = 30;

// Phase 2: session comes from Supabase Auth phone OTP. Phase 1: everyone is
// "verified" — the verify sheet UI runs, but any input passes. No playerId
// here: the stub identity lives in the UI's localStorage, not the backend.
export const getVerifiedSession = async (): Promise<{
	uid: string;
	playerId?: string;
} | null> => {
	return { uid: "local-stub" };
};

// The verify sheet's OTP round-trip, stubbed: no SMS goes out and any token
// verifies. Kept so the sheet drives one code path whichever backend is live.
export const requestOtp = async (_phone: string): Promise<void> => {};

export const verifyOtp = async (_args: {
	phone: string;
	token: string;
	playerId?: string;
}): Promise<{ uid: string }> => {
	return { uid: "local-stub" };
};

export const setPlayerId = async (_playerId: string): Promise<void> => {};

// Mint a join code for a new challenge. Phase 2 inserts a row server-side and
// retries on unique-violation; the stub just needs the tid ≡ code (mod 30)
// invariant, with the rest of the digits random.
export const createChallengeRow = async ({
	tid,
}: {
	tid: number;
	scenarioId: string;
	hostPlayerId: string;
}): Promise<ChallengeRow> => {
	if (!Number.isInteger(tid) || tid < 0 || tid >= NUM_TEAMS) {
		throw new ChallengeCodeError(`Invalid team for a challenge: ${tid}`);
	}
	// 6-digit, no leading zero: [100000, 999999]. Snap down to a multiple of 30,
	// then add tid. Max base is 999960 + 29 < 1e6, min is 100020 - 30 + 0 ≥ 1e5.
	const min = 100020; // smallest multiple of 30 with 6 digits, plus headroom
	const max = 999960;
	const base =
		Math.floor((min + Math.random() * (max - min)) / NUM_TEAMS) * NUM_TEAMS;
	const code = String(base + tid);
	return { code, seed: seedForCode(code), tid };
};

// Resolve a join code to the world it names. Phase 2 is a row fetch (404 on
// unknown, reject on closed); the stub decodes the tid from the code.
export const getChallengeRow = async (code: string): Promise<ChallengeRow> => {
	if (!isValidChallengeCode(code)) {
		throw new ChallengeCodeError(
			`Enter the ${CHALLENGE_CODE_LENGTH}-digit code from your host.`,
		);
	}
	const tid = Number.parseInt(code, 10) % NUM_TEAMS;
	return { code, seed: seedForCode(code), tid };
};

// Submitted results, keyed by code. In-memory only: the local save's own entry
// is always recoverable from the challenge gameAttr, and cross-device entries
// are a Phase 2 backend feature. shareId/expiry are minted here so the result
// gameAttr shape (and the UI on top of it) is already the real one.
const entriesByCode = new Map<string, ChallengeEntry[]>();

const SHARE_TTL_MS = 24 * 60 * 60 * 1000;

export const submitEntry = async (
	entry: ChallengeEntry,
): Promise<{ shareId: string; shareExpiresAt: number }> => {
	const list = entriesByCode.get(entry.code) ?? [];
	// unique(code, auth_uid) server-side; the stub dedupes by playerId.
	if (!list.some((e) => e.playerId === entry.playerId)) {
		list.push(entry);
		entriesByCode.set(entry.code, list);
	}
	return {
		shareId: `local-${entry.code}-${entry.playerId}`,
		shareExpiresAt: Date.now() + SHARE_TTL_MS,
	};
};

// Per-challenge leaderboard, ranked by GM Score. Phase 2 is a scoped select;
// the stub returns whatever this session submitted (callers merge in the local
// save's own entry from the challenge gameAttr).
export const getLeaderboard = async (
	code: string,
): Promise<ChallengeEntry[]> => {
	const list = entriesByCode.get(code) ?? [];
	return [...list].sort((a, b) => b.gmScore - a.gmScore);
};
